import { defineCopy } from '@floorfile/i18n'

/**
 * The design system's own catalogue (localization plan, the `packages/ui`
 * chunk that jumped the Phase 5 queue).
 *
 * ## Why `packages/ui` has a catalogue at all
 *
 * The eslint config next door says these primitives are "near string-free by
 * design", and the intent behind that is right — a composite that hardcodes a
 * word forces 36 features to live with it. What was not true is the "near":
 * nine files were sitting in `copy-baseline.json` and four more carried words
 * the lint rule cannot see (a parameter default, a string inside a ternary).
 * Around fifty user-visible strings, all of them permanently English, in the
 * one package no locale could reach.
 *
 * Three ways out were considered:
 *
 * 1. **Thread every string in as a prop.** Rejected on the same arithmetic that
 *    rejected threading a formatter into `PaginationControls`: that component
 *    alone has 25 call sites, and the 26th silently reverts to English. Worse,
 *    the words here are *generic chrome* — "Clear", "Try again", "Nothing here
 *    yet" — so every one of the 36 features would carry its own copy of them,
 *    a translator would be paid to type them 36 times, and they would drift.
 *    That is precisely how eleven hand-written weekday arrays happened.
 * 2. **Push them into `app/copy.ts`.** `packages/ui` cannot import from
 *    `apps/web`, so this only works via (1).
 * 3. **Give the package its own namespace.** Taken. The package already depends
 *    on `@floorfile/i18n` by an explicit owner decision (2026-08-02), so this
 *    adds a catalogue rather than a dependency.
 *
 * ## The namespace is `ui`, which is the PACKAGE name, not the directory
 *
 * Everywhere else the namespace equals the directory (`features/checks` →
 * `checks`) because `app/locale-catalogues.ts` derives it from the glob path.
 * This catalogue is outside that glob — `import.meta.glob` in `apps/web` cannot
 * see another package — so it is registered *explicitly* by
 * `uiTranslationLoaders` in `./locale-catalogues.ts`, and the namespace is
 * written down in exactly one place rather than derived in two. The guard in
 * `apps/web/src/app/__tests__/copy-catalogues.test.ts` encodes that exception
 * instead of skipping the file.
 *
 * ## What is deliberately NOT here
 *
 * Anything a caller supplies: `EmptyState`'s title, `TrendChip`'s
 * `comparisonLabel`, `DataQualityBadge`'s `detail`, `Lightbox`'s `actionLabel`,
 * and — new in this chunk — `BulkActionBar`'s selection sentence. Those are the
 * feature's words and belong in the feature's catalogue. The rule this chunk
 * establishes for the boundary is in the header of `BulkActionBar.tsx`: a
 * primitive never composes a sentence around a noun it was handed, because the
 * noun's gender and plural live in the caller's language, not in ours.
 */
export const uiCopy = defineCopy('ui', {
  /**
   * `PaginationControls`. `showing` is one message rather than
   * `Showing {from}–{to} of {total}` assembled from three JSX children: the
   * en dash is punctuation a locale chooses, and fr-CA writes the range
   * differently from English.
   */
  pager: {
    showing: 'Showing {from}–{to} of {total}',
    previous: 'Previous',
    next: 'Next',
    page: 'Page {page} of {total}',
    /** The footer variant (mockup): the rows-per-page select and numbered pages. */
    rows: 'Rows',
    rowsPerPage: 'Rows per page',
    goToPage: 'Page {page}',
    navigation: 'Pages',
  },

  /**
   * `BulkActionBar`. The count sentence is NOT here — see the component header.
   * These three are the bar's own furniture and carry no caller noun.
   */
  bulk: {
    region: 'Bulk actions',
    empty: 'No items selected',
    clear: 'Clear',
  },

  /**
   * `ClearFilters`. `cleared` is a plural record because French inflects
   * `filtre`/`filtres` and the verb with it; `applied` is a plural record even
   * though **English does not inflect there** — "(1 applied)" and "(2 applied)"
   * are the same string in English and are not in every language. That is the
   * seventh defect class in the plan: a leaf carrying a count needs
   * `{ one, other }` even when both English forms match.
   */
  filters: {
    clear: 'Clear filters',
    noneApplied: '(no filters applied)',
    applied: { one: '({count} applied)', other: '({count} applied)' },
    cleared: {
      one: 'Cleared {count} filter. Showing all results.',
      other: 'Cleared {count} filters. Showing all results.',
    },
  },

  /** `QueryBoundary` and `QueryErrorNote` — the four query states. */
  query: {
    loading: 'Loading…',
    emptyTitle: 'Nothing here yet',
    errorTitle: 'Could not load this view',
    /** Shown when the failure carried no message of its own. */
    errorFallback: 'The request failed. Check your connection and try again.',
    /** FF-AUDIT-20260914-210459-096: the dead-zone wording. */
    unreachableTitle: 'Can’t reach the server',
    unreachableBody: 'You may be offline. What this device already holds still works; try again when the connection is back.',
    /** Over data the device already holds. */
    staleUnreachable: 'Can’t reach the server — showing what this device has. It refreshes when the connection is back.',
    staleError: 'The last refresh failed — showing what this device has.',
    retry: 'Try again',
    /** FF-QA-144: the API request id under a failed view. */
    reference: 'Ref: {id}',
    noteLabel: 'Could not load this list.',
    noteRetry: 'Retry',
  },

  /**
   * `DataQualityBadge`. Keyed by the `DataQuality` union so adding a level
   * fails the build here rather than rendering a bare code under a KPI.
   */
  /** FF-QA-136: shared not-found block for record pages (`RecordNotFound`). */
  recordNotFound: {
    title: 'Record not found',
    body: 'This record does not exist, or you cannot see it.',
  },

  dataQuality: {
    actual: { short: 'Actual', explain: 'Computed from recorded source data.' },
    partial: {
      short: 'Partial',
      explain: 'Some source records in this range are missing, so the figure understates reality.',
    },
    estimated: {
      short: 'Estimated',
      explain: 'Derived from backfilled or current values because no historical snapshot exists.',
    },
    missing: {
      short: 'No data',
      explain: 'The source data this metric needs has not been entered or imported yet.',
    },
    /** FF-AUDIT-061: what assistive technology reads — one sentence. */
    spoken: '{short}. {explain}',
  },

  /** `ScopeBadge` — the company-wide-not-facility marker. */
  scope: {
    companyWide: 'Company-wide',
    companyWideExplain: 'These records are held at company level and are not attributed to any facility.',
  },

  /** `ProgressMeter`. `valueOf` and `valueNoTarget` are the spoken value. */
  meter: {
    markerLabel: 'Reorder point',
    noTargetSet: 'No target set',
    valueOf: '{value} of {max}',
    valueNoTarget: '{value}, no target configured',
  },

  /** `KpiCard` and `Gauge` — the spoken form of a metric that could not be
   * computed. Both render an em dash visually; the word is screen-reader only,
   * which is exactly why it survived this long in English. */
  metric: {
    unavailable: 'Unavailable',
  },

  /**
   * `TrendChip`'s screen-reader sentence, built in stages rather than
   * concatenated.
   *
   * The old code did `DIRECTION_WORD[d] + ' ' + magnitude + ' ' + comparison +
   * judgement`, which hands a translator four fragments and no control over
   * their order. Each stage below is a whole message with a placeholder, so a
   * language that puts the judgement first, or drops the space before a
   * percentage, can. English is byte-identical:
   * `up` → `up 12.4%` → `up 12.4% vs previous 30 days` →
   * `up 12.4% vs previous 30 days — better`.
   */
  trend: {
    /** The visible word in the chip when movement is inside the flat band. */
    flatShort: 'flat',
    up: 'up {magnitude}',
    down: 'down {magnitude}',
    flat: 'unchanged',
    none: 'No comparison available',
    withComparison: '{movement} {comparison}',
    better: '{movement} — better',
    worse: '{movement} — worse',
  },

  /** `Lightbox`, `AudioAttachment`, `BeforeAfterCard`. */
  media: {
    previousImage: 'Previous image',
    nextImage: 'Next image',
    closeViewer: 'Close image viewer',
    zoom: 'Zoom, currently {scale}×',
    file: 'File',
    capturedBy: 'Captured by',
    captured: 'Captured',
    context: 'Context',
    noTranscript: 'No transcript was captured.',
    before: 'Before',
    after: 'After',
    notCaptured: 'Not captured',
  },

  /** `Dialog`'s scrim, which is a real button and therefore needs a name. */
  dialog: {
    close: 'Close dialog',
    /** FF-AUDIT-066: native `confirm` before a dirty dialog closes. */
    discardChanges: 'Discard your unsaved changes?',
  },

  /**
   * `RowMenu` — the `⋯` overflow at the end of a register row (Fiix audit,
   * design-system spec §8.4). The trigger's name is the only string; the
   * items are the caller's words.
   */
  rowMenu: {
    trigger: 'More actions',
  },

  /** `DataTable` — the column chooser's own furniture (§8.4). */
  table: {
    columns: 'Columns',
    columnsHint: 'Choose the columns this register shows.',
    /** FF-QA-137: the cue on a register wider than its frame. */
    moreColumns: 'More columns →',
    /** FF-QA-137: a column the frame is too narrow for right now (`hideBelow`). */
    hiddenAtWidth: 'needs a wider screen',
    /** FF-QA-095: the tab strip's own "more" cue. */
    moreTabs: 'More tabs',
    resetColumns: 'Reset to default columns',
    /** F27: the column-edge drag handle (a focusable separator). */
    resizeColumn: 'Resize {column} column',
    /** F27: back to the automatic widths. */
    resetWidths: 'Reset column widths',
    /** F1.1: a tree register's chevron, and the phone card's depth line. */
    expandRow: 'Show what is under {name}',
    collapseRow: 'Hide what is under {name}',
    /** Read before a tree row's name (the row itself cannot carry aria-level outside a treegrid). */
    rowLevel: 'Level {level}',
    partOf: 'Part of {name}',
  },

  /** `RecordPage` (§8.4): the header's overflow trigger and the phone-width facts disclosure. */
  record: {
    moreActions: 'More actions',
    details: 'Details',
    /** F1.5: the landmark around an asset's "Line 3 › Filler" trail. */
    breadcrumb: 'Where this sits',
  },

  /** `PropertyList` inline edit (§8.4). `{label}` is the caller's field label. */
  property: {
    edit: 'Edit {label}',
    save: 'Save',
    cancel: 'Cancel',
    saveFailed: 'Could not save.',
  },
})

export type UiCopy = typeof uiCopy.en
